// Ingestion management API client

import { apiRequest } from '@/lib/api';

export interface BackfillRequest {
  buildId: number;
  startDate?: string;
  endDate?: string;
  sources?: Array<'email' | 'chatter'>;
  batchSize?: number;
  models?: string[];
  offset?: number;
  continueJobId?: string;
}

export interface SyncRequest {
  buildId: number;
  sources?: Array<'email' | 'chatter'>;
  models?: string[];
}

export interface ResetRequest {
  buildId?: number;
  sources?: Array<'email' | 'chatter'>;
  confirm: boolean;
}

export interface SyncStatus {
  email?: {
    lastSyncId: number | null;
    lastSyncDate: string | null;
    lastSyncAt: string | null;
    status: 'idle' | 'syncing' | 'error';
    totalMessages: number;
    lastBatchCount: number;
  };
  chatter?: {
    lastSyncId: number | null;
    lastSyncDate: string | null;
    lastSyncAt: string | null;
    status: 'idle' | 'syncing' | 'error';
    totalMessages: number;
    lastBatchCount: number;
  };
  activeJobs?: Array<{
    jobId: string;
    type: 'backfill' | 'incremental' | 'reset';
    status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
    progress: number;
    startedAt: string | null;
  }>;
}

export interface BackfillResult {
  success: boolean;
  complete: boolean;
  synced: {
    emails?: number;
    chatter?: number;
  };
  errors: string[];
  nextBatch?: {
    offset: number;
    startDate?: string;
  };
  jobId: string;
}

export interface IngestionJob {
  id: string;
  project_id: string;
  build_id: number;
  job_type: 'backfill' | 'incremental' | 'reset';
  sources: string[];
  config: any;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  progress_current: number;
  progress_total: number | null;
  progress_percentage: number;
  result: any;
  error_message: string | null;
  started_at: string | null;
  completed_at: string | null;
  duration_ms: number | null;
  created_at: string;
  updated_at: string;
}

/**
 * Start historical backfill of emails and chatter
 */
export async function backfillInsights(
  projectId: string,
  request: BackfillRequest
): Promise<BackfillResult> {
  return apiRequest<BackfillResult>(`/api/ai/agent/projects/${projectId}/insights/backfill`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/**
 * Incremental sync - fetch only new messages since last sync
 */
export async function syncInsights(
  projectId: string,
  request: SyncRequest
): Promise<BackfillResult> {
  return apiRequest<BackfillResult>(`/api/ai/agent/projects/${projectId}/insights/sync`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/**
 * Get sync status for a project/build
 */
export async function getSyncStatus(
  projectId: string,
  buildId: number
): Promise<SyncStatus> {
  return apiRequest<SyncStatus>(
    `/api/ai/agent/projects/${projectId}/insights/sync-status?buildId=${buildId}`
  );
}

/**
 * Reset/delete all insights data
 */
export async function resetInsights(
  projectId: string,
  request: ResetRequest
): Promise<{ success: boolean; deleted: Record<string, number> }> {
  return apiRequest<{ success: boolean; deleted: Record<string, number> }>(
    `/api/ai/agent/projects/${projectId}/insights/reset`,
    {
      method: 'POST',
      body: JSON.stringify(request),
    }
  );
}

/**
 * Get ingestion jobs for a project/build
 */
export async function getIngestionJobs(
  projectId: string,
  buildId: number,
  limit = 50
): Promise<{ jobs: IngestionJob[] }> {
  return apiRequest<{ jobs: IngestionJob[] }>(
    `/api/ai/agent/projects/${projectId}/insights/jobs?buildId=${buildId}&limit=${limit}`
  );
}

/**
 * Cancel or delete an ingestion job
 */
export async function deleteIngestionJob(
  projectId: string,
  jobId: string
): Promise<{ success: boolean }> {
  return apiRequest<{ success: boolean }>(
    `/api/ai/agent/projects/${projectId}/insights/jobs/${jobId}`,
    {
      method: 'DELETE',
    }
  );
}

/**
 * Auto-loop backfill until complete
 */
export async function backfillUntilComplete(
  projectId: string,
  request: BackfillRequest,
  onProgress?: (result: BackfillResult) => void
): Promise<BackfillResult> {
  let result = await backfillInsights(projectId, request);

  if (onProgress) {
    onProgress(result);
  }

  // Keep looping while not complete
  while (!result.complete && result.nextBatch) {
    result = await backfillInsights(projectId, {
      ...request,
      offset: result.nextBatch.offset,
      continueJobId: result.jobId,
    });

    if (onProgress) {
      onProgress(result);
    }

    // Safety: break after 1000 iterations
    if (result.nextBatch && result.nextBatch.offset > 100000) {
      console.error('Backfill safety limit reached');
      break;
    }
  }

  return result;
}
